<template>
  <div class="bg-white">
    <div class="max-w-7xl mx-auto bg-white pt-16 sm:pt-24 sm:px-6 lg:px-8">
      <!-- xs to lg -->
      <div class="max-w-2xl mx-auto space-y-16 lg:hidden">
        <section v-for="(tier, tierIdx) in tiers" :key="tier.name">
          <div class="px-4 mb-8">
            <h2 class="text-lg leading-6 font-medium text-gray-900">{{ tier.name }}</h2>
            <p class="mt-4">
              <span class="text-4xl font-extrabold text-gray-900">{{ tier.priceMonthly }}</span>
              {{ " " }}
              <span class="text-base font-medium text-gray-500">/mo *</span>
            </p>
            <p class="mt-4 text-sm text-gray-500">{{ tier.description }}</p>
            <a
              :href="tier.href"
              class="mt-6 block border border-gray-800 rounded-md bg-gray-800 w-full py-2 text-sm font-semibold text-white text-center hover:bg-gray-900"
              >Buy {{ tier.name }}</a
            >
          </div>

          <table v-for="section in sections" :key="section.name" class="w-full">
            <caption class="bg-gray-50 border-t border-gray-200 py-3 px-4 text-sm font-medium text-gray-900 text-left">
              {{ section.name }}
            </caption>
            <thead>
              <tr>
                <th class="sr-only" scope="col">Feature</th>
                <th class="sr-only" scope="col">Included</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="feature in section.features" :key="feature.name" class="border-t border-gray-200">
                <th class="py-5 px-4 text-sm font-normal text-gray-500 text-left" scope="row">{{ feature.name }}</th>
                <td class="py-5 pr-4">
                  <span
                    v-if="typeof feature.tiers[tier.name] === 'string'"
                    class="block text-sm text-gray-700 text-right"
                    >{{ feature.tiers[tier.name] }}</span
                  >
                  <template v-else>
                    <CheckIcon
                      v-if="feature.tiers[tier.name] === true"
                      class="ml-auto h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <MinusIcon v-else class="ml-auto h-5 w-5 text-gray-400" aria-hidden="true" />
                    <span class="sr-only">{{ feature.tiers[tier.name] === true ? "Yes" : "No" }}</span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>

          <div :class="[tierIdx < tiers.length - 1 ? 'py-5 border-b' : 'pt-5', 'border-t border-gray-200 px-4']">
            <a
              :href="tier.href"
              class="block w-full bg-gray-800 border border-gray-800 rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-gray-900"
              >Buy {{ tier.name }}</a
            >
          </div>
        </section>
      </div>

      <!-- lg+ -->
      <div class="hidden lg:block">
        <table class="w-full h-px table-fixed">
          <caption class="sr-only"> Pricing plan comparison </caption>
          <thead>
            <tr>
              <th class="pb-4 px-6 text-sm font-medium text-gray-900 text-left" scope="col">
                <span class="sr-only">Feature by</span>
                <span>Plans</span>
              </th>
              <!-- NOTE: w-1/6 = 5 tiers => w-1/{tiers.length} -->
              <th
                v-for="tier in tiers"
                :key="tier.name"
                class="w-1/6 pb-4 px-6 text-lg leading-6 font-medium text-gray-900 text-left"
                scope="col"
              >
                {{ tier.name }}
              </th>
            </tr>
          </thead>
          <tbody class="border-t border-gray-200 divide-y divide-gray-200">
            <tr>
              <th class="py-8 px-6 text-sm font-medium text-gray-900 text-left align-top" scope="row">Pricing</th>
              <td v-for="tier in tiers" :key="tier.name" class="h-full py-8 px-6 align-top">
                <div class="relative h-full table">
                  <p>
                    <span class="text-4xl font-extrabold text-gray-900">{{ tier.priceMonthly }}</span>
                    {{ " " }}
                    <span class="text-base font-medium text-gray-500">/mo *</span>
                  </p>
                  <p class="mt-4 mb-16 text-sm text-gray-500">{{ tier.description }}</p>
                  <Dialog :btnText="'Buy ' + tier.name" />
                </div>
              </td>
            </tr>
            <template v-for="section in sections" :key="section.name">
              <tr>
                <th
                  class="bg-gray-50 py-3 pl-6 text-sm font-medium text-gray-900 text-left"
                  :colspan="tiers.length + 1"
                  scope="colgroup"
                >
                  {{ section.name }}
                </th>
              </tr>
              <tr v-for="feature in section.features" :key="feature.name">
                <th class="py-5 px-6 text-sm font-normal text-gray-500 text-left" scope="row">{{ feature.name }}</th>
                <td v-for="tier in tiers" :key="tier.name" class="py-5 px-6">
                  <span v-if="typeof feature.tiers[tier.name] === 'string'" class="block text-sm text-gray-700">{{
                    feature.tiers[tier.name]
                  }}</span>
                  <template v-else>
                    <CheckIcon
                      v-if="feature.tiers[tier.name] === true"
                      class="h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <MinusIcon v-else class="h-5 w-5 text-gray-400" aria-hidden="true" />
                    <span class="sr-only"
                      >{{ feature.tiers[tier.name] === true ? "Included" : "Not included" }} in {{ tier.name }}</span
                    >
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <div class="py-10 w-full text-sm text-gray-500 text-center">
      <p>* exclusive of VAT </p>
      <p class="">
        + Contact
        <a
          href="mailto:sales@spikedata.co.za"
          title="sales@spikedata.co.za"
          class="text-primary hover:text-primary-600 font-bold"
          >sales</a
        >
        to request custom pricing if you have volumes which exceed 3,000 pdfs / month.
      </p>
    </div>
  </div>
</template>

<script>
import { CheckIcon, MinusIcon } from "@heroicons/vue/solid";
import Dialog from "./Dialog.vue";

const tiers = [
  {
    name: "Pay-as-you-go",
    href: "#",
    priceMonthly: "R 0",
    description: "For once-off pdf conversion",
  },
  {
    name: "Individual",
    href: "#",
    priceMonthly: "R 600",
    description: "If you just need pdf conversion and have low volumes to convert",
  },
  {
    name: "Startup",
    href: "#",
    priceMonthly: "R 1,500",
    description: "When you need access to our API",
  },
  {
    name: "Business",
    href: "#",
    priceMonthly: "R 4,000",
    description: "API access and fraud detection ",
  },
  {
    name: "Enterprise",
    href: "#",
    priceMonthly: "R 8,000",
    description: "For higher-volume clients +",
  },
];
const sections = [
  {
    name: "Unit pricing",
    features: [
      {
        name: "Price per pdf",
        tiers: {
          "Pay-as-you-go": "R 16 /pdf *",
          Individual: "R 12 /pdf *",
          Startup: "R 10 /pdf *",
          Business: "R 8 /pdf *",
          Enterprise: "R 5 /pdf *",
        },
      },
      {
        name: "Base allotment",
        tiers: {
          "Pay-as-you-go": "0 /pdfs",
          Individual: "50 /pdfs",
          Startup: "150 /pdfs",
          Business: "500 /pdfs",
          Enterprise: "1,600 /pdfs",
        },
      },
    ],
  },
  {
    name: "Features",
    features: [
      {
        name: "Online converter",
        tiers: {
          "Pay-as-you-go": true,
          Individual: true,
          Startup: true,
          Business: true,
          Enterprise: true,
        },
      },
      {
        name: "Users",
        tiers: {
          "Pay-as-you-go": "1 user",
          Individual: "1 user",
          Startup: "multiple users",
          Business: "multiple users",
          Enterprise: "multiple users",
        },
      },
      {
        name: "API access",
        tiers: {
          "Pay-as-you-go": false,
          Individual: false,
          Startup: true,
          Business: true,
          Enterprise: true,
        },
      },
      {
        name: "Authenticity",
        tiers: {
          "Pay-as-you-go": false,
          Individual: false,
          Startup: false,
          Business: true,
          Enterprise: true,
        },
      },
    ],
  },
  {
    name: "Support",
    features: [
      {
        name: "Best-effort",
        tiers: {
          "Pay-as-you-go": true,
          Individual: true,
          Startup: true,
          Business: true,
          Enterprise: true,
        },
      },
      {
        name: "Priority",
        tiers: {
          "Pay-as-you-go": false,
          Individual: false,
          Startup: "2 emails",
          Business: "4 emails",
          Enterprise: "5 emails",
        },
      },
    ],
  },
];

export default {
  components: {
    CheckIcon,
    MinusIcon,
    Dialog,
  },
  setup() {
    return {
      tiers,
      sections,
    };
  },
};
</script>
